import {
  commentsData, featuresData, cardsData, mediaData,
  topicsData, updatesData, faqData,
  type CommentItem, type FeatureItem, type CardItem,
  type MediaItem, type TopicItem, type UpdateItem, type FaqItem,
} from '@/mocks/homeData';

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="text-center mb-12">
      <h2 className="text-2xl md:text-3xl font-black text-white">{title}</h2>
      {subtitle && <p className="text-surface-400 text-sm mt-3">{subtitle}</p>}
    </div>
  );
}

// ─── #comments 瓜友热评 ───
function CommentsSection() {
  return (
    <section id="comments" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="瓜友热评" subtitle="真实用户反馈 · 吃瓜群众的声音" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {commentsData.map((item: CommentItem) => (
            <div key={item.id} className="p-5 rounded-xl bg-surface-900/60 border border-surface-800/60">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-surface-800">
                  <img src={item.avatar} alt={item.user} className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold">{item.user}</p>
                  <p className="text-surface-500 text-xs">{item.time} · 来自「{item.topic}」</p>
                </div>
              </div>
              <p className="text-surface-300 text-sm leading-relaxed">{item.content}</p>
              <div className="flex items-center gap-1 mt-3 text-surface-500 text-xs">
                <i className="ri-heart-line text-brand-400"></i>
                <span>{item.likes.toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #features 平台优势 ───
function FeaturesSection() {
  return (
    <section id="features" className="py-16 md:py-20 bg-surface-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="平台优势" subtitle="为什么选择每日大赛" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {featuresData.map((item: FeatureItem) => (
            <div key={item.id} className="p-6 rounded-xl bg-surface-800/60 border border-surface-700/30 hover:border-brand-500/20 transition-all duration-200 text-center cursor-pointer">
              <span className="w-14 h-14 flex items-center justify-center rounded-xl bg-brand-500/15 text-brand-400 mx-auto mb-4">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="text-white font-bold text-base mb-2">{item.title}</h3>
              <p className="text-surface-400 text-sm leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #cards 热门吃瓜 ───
function CardsSection() {
  return (
    <section id="cards" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="热门吃瓜" subtitle="编辑精选 · 今日必看" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {cardsData.map((item: CardItem) => (
            <div key={item.id} className="group rounded-xl overflow-hidden bg-surface-900 border border-surface-800/60 hover:border-brand-500/20 transition-all duration-200 cursor-pointer">
              <div className="relative h-44 overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3">
                  <span className="px-2.5 py-1 text-xs font-semibold bg-brand-500 text-white rounded-md">{item.tag}</span>
                </div>
              </div>
              <div className="p-5">
                <h3 className="text-white font-bold text-sm group-hover:text-brand-400 transition-colors duration-200 line-clamp-2">{item.title}</h3>
                <p className="text-surface-400 text-xs mt-2 line-clamp-2">{item.summary}</p>
                <div className="flex items-center justify-between mt-4 text-surface-500 text-xs">
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line"></i> {item.time}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i> {item.views} 阅读
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #media 视频专区 ───
function MediaSection() {
  return (
    <section id="media" className="py-16 md:py-20 bg-surface-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="视频专区" subtitle="精选视频内容 · 吃瓜更直观" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {mediaData.map((item: MediaItem) => (
            <div key={item.id} className="group rounded-xl overflow-hidden bg-surface-800 border border-surface-700/30 hover:border-brand-500/20 transition-all duration-200 cursor-pointer">
              <div className="relative h-44 overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-brand-500/90 text-white group-hover:bg-brand-500 transition-all duration-200 group-hover:scale-110">
                    <i className="ri-play-fill text-xl ml-0.5"></i>
                  </span>
                </div>
                <div className="absolute bottom-2 right-2">
                  <span className="px-2 py-0.5 text-xs font-medium bg-surface-950/80 text-white rounded">
                    {item.duration}
                  </span>
                </div>
                <div className="absolute top-3 left-3">
                  <span className="px-2 py-0.5 text-xs font-semibold bg-brand-500 text-white rounded">{item.tag}</span>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-white font-semibold text-sm group-hover:text-brand-400 transition-colors duration-200 line-clamp-2">{item.title}</h3>
                <p className="text-surface-500 text-xs mt-2 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {item.views} 次播放
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #topics 话题标签 ───
function TopicsSection() {
  return (
    <section id="topics" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="话题标签" subtitle="热门话题聚合 · 一网打尽" />
        <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
          {topicsData.map((item: TopicItem) => (
            <div
              key={item.id}
              className={`group flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer transition-all duration-200 ${
                item.isHot
                  ? 'bg-brand-500/10 border-brand-500/30 hover:bg-brand-500/20 text-brand-400'
                  : 'bg-surface-800/60 border-surface-700/30 hover:border-surface-600 text-surface-300'
              }`}
            >
              {item.isHot && <i className="ri-fire-line text-xs"></i>}
              <span className="text-sm font-medium">{item.name}</span>
              <span className={`text-xs ${item.isHot ? 'text-brand-400/70' : 'text-surface-500'}`}>{item.heat}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #updates 最新动态 ───
function UpdatesSection() {
  return (
    <section id="updates" className="py-16 md:py-20 bg-surface-900">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="最新动态" subtitle="实时更新 · 分秒必争" />
        <div className="max-w-3xl mx-auto space-y-4">
          {updatesData.map((item: UpdateItem) => (
            <div key={item.id} className="flex items-start gap-4 p-4 rounded-xl bg-surface-800/60 border border-surface-700/30 hover:border-brand-500/20 transition-all duration-200 cursor-pointer">
              <div className="flex-shrink-0 flex flex-col items-center">
                <div className={`w-2.5 h-2.5 rounded-full mt-1.5 ${item.isNew ? 'bg-brand-500 animate-pulse-slow' : 'bg-surface-600'}`}></div>
                <div className="w-px h-full bg-surface-700/50 mt-1"></div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-surface-500 text-xs">{item.time}</span>
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded ${item.isNew ? 'bg-brand-500/20 text-brand-400' : 'bg-surface-700 text-surface-400'}`}>
                    {item.tag}
                  </span>
                </div>
                <p className="text-white text-sm font-medium">{item.content}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── #faq 常见问答 ───
function FaqSection() {
  return (
    <section id="faq" className="py-16 md:py-20 bg-surface-950">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeading title="常见问答" subtitle="关于每日大赛 · 你想知道的都在这里" />
        <div className="max-w-3xl mx-auto space-y-4">
          {faqData.map((item: FaqItem) => (
            <details key={item.id} className="group rounded-xl bg-surface-900/60 border border-surface-800/60 cursor-pointer">
              <summary className="flex items-center justify-between p-5 list-none">
                <h3 className="text-white text-sm md:text-base font-semibold pr-4">{item.question}</h3>
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-surface-400 group-open:rotate-45 transition-transform duration-200">
                  <i className="ri-add-line"></i>
                </span>
              </summary>
              <div className="px-5 pb-5">
                <p className="text-surface-400 text-sm leading-relaxed">{item.answer}</p>
              </div>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionsGroup2() {
  return (
    <>
      <CommentsSection />
      <FeaturesSection />
      <CardsSection />
      <MediaSection />
      <TopicsSection />
      <UpdatesSection />
      <FaqSection />
    </>
  );
}